import { useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { ReactReduxContext } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import { getHandlerTableChange } from "./Pagination/TablePaginationHelpers";
import overlayFactory from "react-bootstrap-table2-overlay";
import PropTypes from 'prop-types';
import { announce } from "../LiveRegion/liveAnnouncer";
import { Localize as AriaLocalize } from "../../../shared/ariaLabelLocalize";

const ARIA_SORT = { asc: "ascending", desc: "descending" };

const loadingOverlay = overlayFactory({ spinner: true });
const overlayByLoading = {};
const overlay = (loading) => {
  overlayByLoading[loading] = overlayByLoading[loading] || loadingOverlay(loading);
  return overlayByLoading[loading];
};

const BootstrapDataTable = ({
  tableData,
  paginationTableProps,
  onTableChangeFunction,
  isLoading,
  noDataMessage,
  defaultSorted,
  columns,
  tableKey,
  expandRow,
  hidePagination = false,
  rowClasses,
  ariaLabel
}) => {
  const remoteProp = !hidePagination ? { remote: true } : {};
  const tableId = useId();
  const reduxContext = useContext(ReactReduxContext);
  const ariaLangId = reduxContext?.store?.getState()?.language?.selectedlanguage?.id;
  const sortFocusRef = useRef(null);
  const mountedRef = useRef(false);
  const columnsVersionRef = useRef(0);
  const [sortKey, setSortKey] = useState(
    defaultSorted?.[0] ? `${defaultSorted[0].dataField}|${defaultSorted[0].order}` : ""
  );

  const a11yColumns = useMemo(() => {
    const [sortField, sortOrder] = sortKey.split("|");
    const columnsVersion = ++columnsVersionRef.current;
    return columns.map((column, index) => {
      const ownHeaderAttrs = column.headerAttrs;
      const labelId = `${tableId}-th-${index}`;
      const a11yAttrs = {
        tabIndex: -1,
        ...(column.sort && {
          "aria-labelledby": labelId,
          "aria-sort": sortField === column.dataField ? ARIA_SORT[sortOrder] : undefined,
        }),
      };
      const headerAttrs = typeof ownHeaderAttrs === "function"
        ? (col, colIndex) => ({ ...ownHeaderAttrs(col, colIndex), ...a11yAttrs })
        : { ...ownHeaderAttrs, ...a11yAttrs };
      const formatExtraData = column.formatter
        ? { ...column.formatExtraData, columnsVersion }
        : column.formatExtraData;
      if (!column.sort) return { ...column, headerAttrs, formatExtraData };
      return {
        ...column,
        headerAttrs,
        formatExtraData,
        onSort: (field, order) => {
          if (!mountedRef.current) {
            column.onSort?.(field, order);
            return;
          }
          if (document.activeElement?.closest?.("th")?.getAttribute("aria-labelledby") === labelId) {
            const pending = { labelId, loadingSeen: false };
            sortFocusRef.current = pending;
            setTimeout(() => {
              if (sortFocusRef.current === pending && !pending.loadingSeen) sortFocusRef.current = null;
            }, 1500);
          }
          setSortKey(`${field}|${order}`);
          const t = AriaLocalize[ariaLangId] || AriaLocalize['en_US'];
          if (typeof column.text === "string" && column.text) {
            announce(t[order === "asc" ? "sorted_ascending" : "sorted_descending"].replace("{column}", column.text));
          }
          column.onSort?.(field, order);
        },
        headerFormatter: (col, colIndex, components) => (
          <button type="button" className="th-sort-btn" onKeyUp={(e) => e.stopPropagation()}>
            <span id={labelId}>
              {column.headerFormatter ? column.headerFormatter(col, colIndex, components) : column.text}
            </span>
            {components.sortElement}
          </button>
        ),
      };
    });
  }, [columns, sortKey, tableId, ariaLangId]);

  const a11yExpandRow = useMemo(() => {
    if (!expandRow?.expandColumnRenderer) return expandRow;
    const t = AriaLocalize[ariaLangId] || AriaLocalize['en_US'];
    return {
      ...expandRow,
      expandHeaderColumnRenderer: () => <span className="visually-hidden">{t.row_details}</span>,
      expandColumnRenderer: (props) => (
        <button
          type="button"
          className="btn-unstyled table-row-expand-toggle"
          aria-expanded={props.expanded}
          aria-label={props.expanded ? t.hide_row_details : t.show_row_details}
        >
          <span aria-hidden="true">{expandRow.expandColumnRenderer(props)}</span>
        </button>
      ),
    };
  }, [expandRow, ariaLangId]);

  useEffect(() => {
    const table = document.getElementById(`${tableId}-table`);
    if (!table) return;
    if (ariaLabel) table.setAttribute("aria-label", ariaLabel);
    else table.removeAttribute("aria-label");
  }, [ariaLabel, tableId, isLoading]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const pending = sortFocusRef.current;
    if (!pending) return;
    if (isLoading) {
      pending.loadingSeen = true;
      return;
    }
    if (!pending.loadingSeen) return;
    sortFocusRef.current = null;
    const button = document.getElementById(pending.labelId)?.closest("button");
    if (button && (!document.activeElement || document.activeElement === document.body)) button.focus();
  }, [isLoading]);

  return (
    <BootstrapTable
      wrapperClasses="sortable-table-box"
      bordered={false}
      classes="table cmn-table table-hover align-middle margin-bottom-0 cmn-sortable-table"
      id={`${tableId}-table`}
      keyField={tableKey}
      data={tableData === null ? [] : tableData}
      bootstrap4
      onTableChange={getHandlerTableChange(onTableChangeFunction)}
      noDataIndication={() => {
        return isLoading ? (
          <></>
        ) : (
          <div className="no-data-found">{noDataMessage}</div>
        );
      }}
      columns={a11yColumns}
      loading={isLoading}
      defaultSorted={defaultSorted}
      overlay={overlay}
      expandRow={a11yExpandRow}
      rowClasses={rowClasses}
      {...remoteProp}
      {...paginationTableProps}
    ></BootstrapTable>
  );
};

BootstrapDataTable.propTypes = {
  tableData: PropTypes.array,
  paginationTableProps: PropTypes.object,
  onTableChangeFunction: PropTypes.func,
  isLoading: PropTypes.bool,
  noDataMessage: PropTypes.string,
  defaultSorted: PropTypes.array,
  columns: PropTypes.array,
  tableKey: PropTypes.string,
  expandRow: PropTypes.object,
  hidePagination: PropTypes.bool,
  rowClasses: PropTypes.func,
  ariaLabel: PropTypes.string,
}

export default BootstrapDataTable;
